import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

const SORT_ASC = 'asc';
const SORT_DESC = 'desc';

const toPositiveInt = (value, fallback) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const parseSort = (value) => {
  if (!value || typeof value !== 'string') return {};
  const [field, direction] = value.split(':');
  if (!field || (direction !== SORT_ASC && direction !== SORT_DESC)) return {};
  return { [field]: direction === SORT_ASC ? 1 : -1 };
};

const serializeSort = (sortOptions) => {
  if (!sortOptions || typeof sortOptions !== 'object') return '';
  const activeSort = Object.entries(sortOptions).find(([, value]) => value === 1 || value === -1);
  if (!activeSort) return '';
  const [field, direction] = activeSort;
  return `${field}:${direction === 1 ? SORT_ASC : SORT_DESC}`;
};

const parseJsonParam = (value, fallback) => {
  if (!value) return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed ?? fallback;
  } catch (error) {
    return fallback;
  }
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined || value === '') return false;

  if (Array.isArray(value)) {
    return value.some((item) => hasMeaningfulValue(item));
  }

  if (typeof value === 'object') {
    return Object.values(value).some((item) => hasMeaningfulValue(item));
  }

  return true;
};

const defaultParseExtraFilters = (rawValue, fallback) => parseJsonParam(rawValue, fallback);
const defaultSerializeExtraFilters = (value) => JSON.stringify(value);
const isPlainObject = (value) => value && typeof value === 'object' && !Array.isArray(value);

const shallowEqualObjects = (a, b) => {
  if (a === b) return true;
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => a[key] === b[key]);
};

const buildPaginationState = (pageNumber, rowsPerPage, setPaginationRef) => ({
  pageNumber,
  rowsPerPage,
  maxVisiblePageItems: 6,
  onUpdate: (value) => {
    const nextPage = value === -1 ? 0 : value;
    if (typeof setPaginationRef?.current === 'function') {
      setPaginationRef.current((prev) => ({ ...prev, pageNumber: nextPage }));
    }
  },
  showPageNumberInput: 6,
});

const resetPaginationPage = (setPagination) => {
  setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
};

export const useListQueryState = ({
  defaultFilter,
  defaultSortOptions = {},
  defaultRowsPerPage = 12,
  params = {
    search: 'search',
    page: 'page',
    pageSize: 'pageSize',
    sort: 'sort',
  },
  extraFiltersParam = '',
  defaultExtraFilters = {},
  parseExtraFilters = defaultParseExtraFilters,
  serializeExtraFilters = defaultSerializeExtraFilters,
  resetPageOnSearchChange = true,
  resetPageOnExtraFiltersChange = true,
}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultFilterRef = useRef(defaultFilter);
  const defaultSortOptionsRef = useRef(defaultSortOptions);
  const defaultRowsPerPageRef = useRef(defaultRowsPerPage);
  const defaultExtraFiltersRef = useRef(defaultExtraFilters);
  const isSyncingFromUrlRef = useRef(false);

  const parsedState = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchText = urlParams.get(params.search) || '';
    const pageNumber = toPositiveInt(urlParams.get(params.page), 1) - 1;
    const rowsPerPage = toPositiveInt(
      urlParams.get(params.pageSize),
      defaultRowsPerPageRef.current,
    );
    const sortOptions = parseSort(urlParams.get(params.sort));
    const extraFilters = extraFiltersParam
      ? parseExtraFilters(urlParams, extraFiltersParam, defaultExtraFiltersRef.current)
      : defaultExtraFiltersRef.current;
    return { searchText, pageNumber, rowsPerPage, sortOptions, extraFilters };
  }, [
    extraFiltersParam,
    location.search,
    params.page,
    params.pageSize,
    params.search,
    params.sort,
    parseExtraFilters,
  ]);

  const [filter, setFilterState] = useState({
    ...defaultFilterRef.current,
    searchText: parsedState.searchText,
  });
  const [sortOptions, setSortOptions] = useState(
    Object.keys(parsedState.sortOptions).length > 0
      ? parsedState.sortOptions
      : defaultSortOptionsRef.current,
  );
  const setPaginationRef = useRef(null);
  const [pagination, setPaginationState] = useState(() =>
    buildPaginationState(parsedState.pageNumber, parsedState.rowsPerPage, setPaginationRef),
  );
  const [extraFilters, setExtraFiltersState] = useState(parsedState.extraFilters);
  const [isHydrated, setIsHydrated] = useState(false);

  const setPagination = useCallback((updater) => {
    setPaginationState(updater);
  }, []);

  const setFilter = useCallback(
    (updater) => {
      setFilterState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const searchChanged = (next?.searchText ?? '') !== (prev?.searchText ?? '');
        if (resetPageOnSearchChange && searchChanged && !isSyncingFromUrlRef.current) {
          resetPaginationPage(setPaginationState);
        }
        return next;
      });
    },
    [resetPageOnSearchChange],
  );

  const setExtraFilters = useCallback(
    (updater) => {
      setExtraFiltersState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const filtersChanged = JSON.stringify(next) !== JSON.stringify(prev);
        if (resetPageOnExtraFiltersChange && filtersChanged && !isSyncingFromUrlRef.current) {
          resetPaginationPage(setPaginationState);
        }
        return next;
      });
    },
    [resetPageOnExtraFiltersChange],
  );

  useEffect(() => {
    setPaginationRef.current = setPaginationState;
  }, []);

  const isMountRef = useRef(true);
  const skipUrlWriteRef = useRef(false);
  const hasSyncedFromUrlRef = useRef(false);

  useLayoutEffect(() => {
    isSyncingFromUrlRef.current = true;

    setFilterState((prev) =>
      prev?.searchText === parsedState.searchText
        ? prev
        : { ...prev, searchText: parsedState.searchText },
    );

    const nextSortOptions =
      Object.keys(parsedState.sortOptions).length > 0
        ? parsedState.sortOptions
        : defaultSortOptionsRef.current;
    setSortOptions((prev) => (shallowEqualObjects(prev, nextSortOptions) ? prev : nextSortOptions));

    setPaginationState((prev) => {
      if (
        prev.pageNumber === parsedState.pageNumber &&
        prev.rowsPerPage === parsedState.rowsPerPage
      ) {
        return prev;
      }
      return buildPaginationState(
        parsedState.pageNumber,
        parsedState.rowsPerPage,
        setPaginationRef,
      );
    });

    setExtraFiltersState((prev) =>
      JSON.stringify(prev) === JSON.stringify(parsedState.extraFilters)
        ? prev
        : parsedState.extraFilters,
    );

    isSyncingFromUrlRef.current = false;
    // Skip one state→URL write after URL navigation so stale pagination is not written back.
    if (hasSyncedFromUrlRef.current) {
      skipUrlWriteRef.current = true;
    }
    hasSyncedFromUrlRef.current = true;
    setIsHydrated(true);
  }, [
    extraFiltersParam,
    location.pathname,
    location.search,
    parsedState.extraFilters,
    parsedState.pageNumber,
    parsedState.rowsPerPage,
    parsedState.searchText,
    parsedState.sortOptions,
  ]);

  const updateSearchParams = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSearchText = filter?.searchText || '';
    const nextPage = String((pagination?.pageNumber || 0) + 1);
    const nextPageSize = String(pagination?.rowsPerPage || defaultRowsPerPageRef.current);
    const nextSort = serializeSort(sortOptions);

    if (nextSearchText) nextParams.set(params.search, nextSearchText);
    else nextParams.delete(params.search);

    if (nextPage === '1') nextParams.delete(params.page);
    else nextParams.set(params.page, nextPage);

    if (nextPageSize === String(defaultRowsPerPageRef.current)) nextParams.delete(params.pageSize);
    else nextParams.set(params.pageSize, nextPageSize);

    if (nextSort) nextParams.set(params.sort, nextSort);
    else nextParams.delete(params.sort);

    if (extraFiltersParam) {
      if (!hasMeaningfulValue(extraFilters)) {
        nextParams.delete(extraFiltersParam);
        const serialized = serializeExtraFilters(extraFilters, extraFiltersParam);
        if (isPlainObject(serialized)) {
          Object.keys(serialized).forEach((key) => nextParams.delete(key));
        }
      } else {
        const serialized = serializeExtraFilters(extraFilters, extraFiltersParam);
        if (isPlainObject(serialized)) {
          nextParams.delete(extraFiltersParam);
          Object.entries(serialized).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '') nextParams.delete(key);
            else nextParams.set(key, String(value));
          });
        } else if (serialized === null || serialized === undefined || serialized === '') {
          nextParams.delete(extraFiltersParam);
        } else {
          nextParams.set(extraFiltersParam, serialized);
        }
      }
    }

    const current = searchParams.toString();
    const next = nextParams.toString();
    if (current !== next) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    filter,
    pagination?.pageNumber,
    pagination?.rowsPerPage,
    params.page,
    params.pageSize,
    params.search,
    params.sort,
    extraFilters,
    extraFiltersParam,
    serializeExtraFilters,
    searchParams,
    setSearchParams,
    sortOptions,
  ]);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    if (skipUrlWriteRef.current) {
      skipUrlWriteRef.current = false;
      return;
    }
    updateSearchParams();
  }, [updateSearchParams]);

  return {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
    isHydrated,
  };
};
